import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Phone, Check, RefreshCw, Shield, Loader2, Pencil, LogOut, X } from 'lucide-react';
import api from '../lib/api';

export default function Verification() {
    const navigate = useNavigate();
    const { user, updateUser, logout } = useAuth();
    const [emailCode, setEmailCode] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [verifyingPhone, setVerifyingPhone] = useState(false);
    const [resending, setResending] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [localUser, setLocalUser] = useState(null);
    const initRef = useRef(false);
    
    // Phone entry state (for Google users without phone)
    const [phoneStep, setPhoneStep] = useState('loading'); // 'loading' | 'enter_phone' | 'verify_code'
    const [newPhone, setNewPhone] = useState('');
    const [savingPhone, setSavingPhone] = useState(false);
    
    // Edit phone (for users who entered wrong number)
    const [editingPhone, setEditingPhone] = useState(false);
    
    // Cooldown timers
    const [emailCooldown, setEmailCooldown] = useState(0);
    const [phoneCooldown, setPhoneCooldown] = useState(0);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        
        const initializeVerification = async () => {
            const storedToken = localStorage.getItem('receppet_token');
            if (!storedToken) {
                navigate('/login');
                return;
            }
            
            try {
                const response = await api.get('/auth/me');
                const freshUser = response.data;
                
                setLocalUser(freshUser);
                updateUser(freshUser);
                setEmailVerified(freshUser.email_verified || false);
                setPhoneVerified(freshUser.phone_verified || false);
                
                // Determine phone step based on user state
                if (freshUser.phone_verified) {
                    setPhoneStep('verify_code'); // Already done
                } else if (!freshUser.phone || freshUser.phone.trim() === '') {
                    setPhoneStep('enter_phone'); // No phone → ask for it
                } else {
                    setPhoneStep('verify_code'); // Has phone → show code input
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    localStorage.removeItem('receppet_token');
                    localStorage.removeItem('receppet_user');
                }
                navigate('/login');
            } finally {
                setInitializing(false);
            }
        };
        
        initializeVerification();
    }, [navigate, updateUser]);

    // Cooldown timers
    useEffect(() => {
        if (emailCooldown > 0) {
            const timer = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [emailCooldown]);

    useEffect(() => {
        if (phoneCooldown > 0) {
            const timer = setTimeout(() => setPhoneCooldown(phoneCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [phoneCooldown]);

    // Auto-redirect when both verified
    useEffect(() => {
        if (redirecting || initializing) return;
        if (!emailVerified || !phoneVerified) return;
        
        setRedirecting(true);
        toast.success('Conta verificada com sucesso!');
        
        const completeVerification = async () => {
            try {
                const response = await api.get('/auth/me');
                const freshUser = response.data;
                updateUser(freshUser);
                
                const userType = freshUser?.user_type;
                let dashboardRoute;
                if (userType === 'admin') {
                    dashboardRoute = '/admin';
                } else if (['host', 'caregiver', 'driver', 'partner', 'provider'].includes(userType)) {
                    dashboardRoute = (freshUser?.is_also_tutor || freshUser?.is_dual_profile) ? '/tutor' : '/provider';
                } else {
                    dashboardRoute = '/tutor';
                }
                
                setTimeout(() => navigate(dashboardRoute, { replace: true }), 1000);
            } catch {
                setRedirecting(false);
                navigate('/login');
            }
        };
        
        completeVerification();
    }, [emailVerified, phoneVerified, navigate, updateUser, redirecting, initializing]);

    const handleVerifyEmail = async () => {
        if (!emailCode) { toast.error('Digite o código de verificação'); return; }
        setVerifyingEmail(true);
        try {
            await api.post('/auth/verify-email', { code: emailCode });
            const response = await api.get('/auth/me');
            updateUser(response.data);
            setLocalUser(response.data);
            setEmailVerified(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Código inválido'));
        } finally {
            setVerifyingEmail(false);
        }
    };

    const handleVerifyPhone = async () => {
        if (!phoneCode) { toast.error('Digite o código de verificação'); return; }
        setVerifyingPhone(true);
        try {
            await api.post('/auth/verify-phone', { code: phoneCode });
            const response = await api.get('/auth/me');
            updateUser(response.data);
            setLocalUser(response.data);
            setPhoneVerified(true);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Código inválido'));
        } finally {
            setVerifyingPhone(false);
        }
    };

    const handleResend = async (type) => {
        if (type === 'email' && emailCooldown > 0) return;
        if (type === 'phone' && phoneCooldown > 0) return;
        
        setResending(true);
        try {
            const response = await api.post(`/auth/resend-verification?verification_type=${type}`);
            if (type === 'email') setEmailCooldown(60);
            else setPhoneCooldown(60);
            
            toast.success('Código reenviado!');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao reenviar código'));
        } finally {
            setResending(false);
        }
    };

    // Save phone number (for Google users) → then send SMS
    const handleSavePhone = async () => {
        const cleanPhone = newPhone.replace(/\D/g, '');
        if (cleanPhone.length < 10 || cleanPhone.length > 11) {
            toast.error('Digite um número de telefone válido (DDD + número)');
            return;
        }
        
        setSavingPhone(true);
        try {
            await api.put('/users/me', { phone: cleanPhone });
            
            // Send SMS verification
            await api.post('/auth/resend-verification?verification_type=phone');
            setPhoneCooldown(60);
            
            // Refresh user data
            const response = await api.get('/auth/me');
            updateUser(response.data);
            setLocalUser(response.data);
            
            // Transition to code verification step
            setPhoneStep('verify_code');
            setEditingPhone(false);
            toast.success('Telefone salvo! Código SMS enviado.');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao salvar telefone'));
        } finally {
            setSavingPhone(false);
        }
    };

    const handleExit = () => {
        localStorage.removeItem('receppet_token');
        localStorage.removeItem('receppet_user');
        localStorage.removeItem('receppet_active_panel');
        if (logout) logout();
        toast.info('Sessão encerrada.');
        navigate('/');
    };

    const displayUser = localUser || user;
    const isGoogleUser = displayUser?.auth_provider === 'google';

    if (!displayUser || initializing) {
        return (
            <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-[#4A90B2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="verification-page">
            <Card className="w-full max-w-lg shadow-xl border-0">
                <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 bg-gradient-to-r from-[#4A90B2] to-[#7AB89C] rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold font-['Nunito']">
                        {isGoogleUser && phoneStep === 'enter_phone' ? 'Complete seu Cadastro' : 'Verificar sua Conta'}
                    </CardTitle>
                    <CardDescription>
                        {isGoogleUser && phoneStep === 'enter_phone'
                            ? 'Precisamos do seu telefone para finalizar o cadastro'
                            : 'Para sua segurança, confirme seu e-mail e telefone'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Info Banner */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-600">
                                {isGoogleUser && phoneStep === 'enter_phone'
                                    ? 'Seu e-mail já foi verificado pelo Google. Agora cadastre seu WhatsApp/telefone para receber notificações importantes sobre suas reservas.'
                                    : 'Enviamos códigos para seu e-mail e telefone. Verifique também a caixa de spam.'}
                            </p>
                        </div>
                    </div>

                    {/* EMAIL VERIFICATION — skip for Google users (already verified) */}
                    {!isGoogleUser && (
                        <div className={`p-4 rounded-2xl border-2 transition-all ${emailVerified ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${emailVerified ? 'bg-green-500' : 'bg-[#4A90B2]'}`}>
                                    {emailVerified ? <Check className="w-5 h-5 text-white" /> : <Mail className="w-5 h-5 text-white" />}
                                </div>
                                <div>
                                    <p className="font-semibold text-slate-800">E-mail</p>
                                    <p className="text-sm text-slate-500">{displayUser?.email}</p>
                                </div>
                            </div>
                            
                            {!emailVerified ? (
                                <div className="space-y-3">
                                    <p className="text-sm text-slate-600">Digite o código de 6 dígitos enviado para seu e-mail.</p>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="000000"
                                            value={emailCode}
                                            onChange={(e) => setEmailCode(e.target.value.toUpperCase())}
                                            maxLength={6}
                                            className="text-center text-lg tracking-widest"
                                            data-testid="email-code-input"
                                        />
                                        <Button onClick={handleVerifyEmail} disabled={verifyingEmail} className="btn-primary" data-testid="verify-email-btn">
                                            {verifyingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verificar'}
                                        </Button>
                                    </div>
                                    <button
                                        onClick={() => handleResend('email')}
                                        disabled={resending || emailCooldown > 0}
                                        className={`flex items-center gap-1 text-sm ${emailCooldown > 0 ? 'text-slate-400' : 'text-[#4A90B2] hover:underline'}`}
                                        data-testid="resend-email-btn"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                                        {emailCooldown > 0 ? `Aguarde ${emailCooldown}s` : 'Reenviar código'}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                                    <Check className="w-4 h-4" /> E-mail verificado!
                                </p>
                            )}
                        </div>
                    )}

                    {/* Google user: show email as already verified */}
                    {isGoogleUser && (
                        <div className="p-4 rounded-2xl border-2 bg-green-50 border-green-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-500">
                                    <Check className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="font-semibold text-slate-800">E-mail</p>
                                    <p className="text-sm text-slate-500">{displayUser?.email}</p>
                                </div>
                                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                    Google
                                </span>
                            </div>
                            <p className="text-sm text-green-600 font-medium flex items-center gap-2 mt-2">
                                <Check className="w-4 h-4" /> Verificado via Google
                            </p>
                        </div>
                    )}

                    {/* PHONE SECTION */}
                    <div className={`p-4 rounded-2xl border-2 transition-all ${phoneVerified ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${phoneVerified ? 'bg-green-500' : 'bg-[#4A90B2]'}`}>
                                {phoneVerified ? <Check className="w-5 h-5 text-white" /> : <Phone className="w-5 h-5 text-white" />}
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold text-slate-800">Telefone / WhatsApp</p>
                                {phoneStep === 'verify_code' && displayUser?.phone && (
                                    <p className="text-sm text-slate-500">{displayUser.phone}</p>
                                )}
                            </div>
                            {/* Edit phone button (only when in verify_code step) */}
                            {!phoneVerified && phoneStep === 'verify_code' && !editingPhone && (
                                <button
                                    onClick={() => { setEditingPhone(true); setNewPhone(displayUser?.phone || ''); }}
                                    className="text-[#4A90B2] hover:text-[#3a7a9e] p-2"
                                    title="Editar número"
                                    data-testid="edit-phone-btn"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        
                        {/* STEP 1: Enter phone number (Google users without phone, or editing) */}
                        {(phoneStep === 'enter_phone' || editingPhone) && !phoneVerified && (
                            <div className="space-y-3">
                                <p className="text-sm text-slate-600">
                                    {phoneStep === 'enter_phone'
                                        ? 'Informe seu número de celular para receber o código de verificação por SMS.'
                                        : 'Corrigir número de telefone:'}
                                </p>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="(11) 99999-9999"
                                        value={newPhone}
                                        onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))}
                                        maxLength={11}
                                        className="flex-1"
                                        autoFocus
                                        data-testid="phone-number-input"
                                    />
                                    <Button
                                        onClick={handleSavePhone}
                                        disabled={savingPhone}
                                        className="btn-primary"
                                        data-testid="save-phone-btn"
                                    >
                                        {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar SMS'}
                                    </Button>
                                    {editingPhone && (
                                        <Button variant="ghost" size="sm" onClick={() => { setEditingPhone(false); setNewPhone(''); }}>
                                            <X className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* STEP 2: Verify SMS code */}
                        {phoneStep === 'verify_code' && !phoneVerified && !editingPhone && (
                            <div className="space-y-3">
                                <p className="text-sm text-slate-600">
                                    Digite o código de 6 dígitos enviado por SMS.
                                </p>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="000000"
                                        value={phoneCode}
                                        onChange={(e) => setPhoneCode(e.target.value.toUpperCase())}
                                        maxLength={6}
                                        className="text-center text-lg tracking-widest"
                                        data-testid="phone-code-input"
                                    />
                                    <Button onClick={handleVerifyPhone} disabled={verifyingPhone} className="btn-primary" data-testid="verify-phone-btn">
                                        {verifyingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verificar'}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={() => handleResend('phone')}
                                        disabled={resending || phoneCooldown > 0}
                                        className={`flex items-center gap-1 text-sm ${phoneCooldown > 0 ? 'text-slate-400' : 'text-[#4A90B2] hover:underline'}`}
                                        data-testid="resend-phone-btn"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                                        {phoneCooldown > 0 ? `Aguarde ${phoneCooldown}s` : 'Reenviar código'}
                                    </button>
                                    <button
                                        onClick={() => { setEditingPhone(true); setNewPhone(displayUser?.phone || ''); }}
                                        className="text-sm text-orange-600 hover:underline"
                                    >
                                        Errou o número?
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Done */}
                        {phoneVerified && (
                            <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                                <Check className="w-4 h-4" /> Telefone verificado!
                            </p>
                        )}
                    </div>

                    {/* Trust message */}
                    <div className="text-center p-4 bg-[#F0F9FF] rounded-xl">
                        <p className="text-sm text-[#4A90B2]">
                            A verificação garante a segurança da comunidade RecepPet. Seus dados estão protegidos conforme a LGPD.
                        </p>
                    </div>

                    {/* Exit button */}
                    <div className="pt-4 border-t border-slate-200">
                        <button
                            onClick={handleExit}
                            className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-red-500 py-2 transition-colors"
                            data-testid="exit-verification-btn"
                        >
                            <LogOut className="w-4 h-4" />
                            Sair e tentar novamente depois
                        </button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
